import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from '@mui/material';
import {
  Star as VipIcon,
  PersonAdd as AddIcon,
  AccessTime as ExpiringIcon,
  TrendingUp as UpgradeIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import {
  StatCard,
  SectionLoading,
  SectionError,
  COLORS,
  formatNumber,
  formatCurrency,
  formatPct,
} from './shared';

interface VipData {
  counts: {
    active: number;
    expired: number;
    cancelled: number;
    new_30d: number;
    new_90d: number;
    expiring_30d: number;
    expiring_60d: number;
    expiring_90d: number;
  };
  upgrade: {
    repeat_buyers: number;
    repeat_buyer_vips: number;
    upgrade_rate: number;
  };
  behavior: {
    vip: {
      customers: number;
      orders: number;
      revenue: number;
      avg_order_value: number;
      orders_per_customer: number;
    } | null;
    non_vip: {
      customers: number;
      orders: number;
      revenue: number;
      avg_order_value: number;
      orders_per_customer: number;
    } | null;
  };
  revenue_share: {
    total_90d: number;
    vip_90d: number;
    vip_share_pct: number;
  };
}

interface RenewalCustomer {
  id: number;
  name: string;
  email: string;
  phone: string;
  started_at: string;
  expires_at: string;
  days_until_expiry: number;
}

interface VipFunnelData {
  daily_views: Array<{ date: string; views: number; unique_sessions: number }>;
  source_breakdown: Array<{ utm_source: string; utm_medium: string; sessions: number }>;
  funnel: { view_sessions: number; attempts: number; paid: number };
}

const VipSection: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<VipData | null>(null);
  const [renewals, setRenewals] = useState<RenewalCustomer[]>([]);
  const [funnel, setFunnel] = useState<VipFunnelData | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get('/api/retention/vip'),
      api.get('/api/retention/vip/renewals?days=60'),
      api.get('/api/retention/vip/funnel'),
    ])
      .then(([v, r, f]) => {
        setData(v.data);
        setRenewals(r.data);
        setFunnel(f.data);
      })
      .catch((err) => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} />;
  if (!data) return <SectionError />;

  const { counts, upgrade, behavior, revenue_share } = data;
  const vipLift =
    behavior.vip && behavior.non_vip && behavior.non_vip.orders_per_customer > 0
      ? +(behavior.vip.orders_per_customer / behavior.non_vip.orders_per_customer).toFixed(2)
      : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Headline */}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="活躍 VIP"
            value={formatNumber(counts.active)}
            subtitle={`過去 30 天新加 ${counts.new_30d}，90 天新加 ${counts.new_90d}`}
            icon={<VipIcon />}
            color={COLORS.vip}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="VIP 升級率"
            value={formatPct(upgrade.upgrade_rate)}
            subtitle={`2+ 次購買中 ${upgrade.repeat_buyer_vips}/${upgrade.repeat_buyers} 是 VIP`}
            icon={<UpgradeIcon />}
            color={COLORS.success}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="90 天 VIP 營收佔比"
            value={formatPct(revenue_share.vip_share_pct)}
            subtitle={formatCurrency(revenue_share.vip_90d)}
            icon={<AddIcon />}
            color={COLORS.purple}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="30 天內到期"
            value={formatNumber(counts.expiring_30d)}
            subtitle={`60 天內 ${counts.expiring_60d}，90 天內 ${counts.expiring_90d}`}
            icon={<ExpiringIcon />}
            color={COLORS.warning}
          />
        </Grid>
      </Grid>

      {/* VIP vs Non-VIP */}
      <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}` }}>
        <CardHeader
          title="VIP 行為對比（過去 180 天）"
          titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
          subheader={vipLift > 0 ? `VIP 訂單頻率是非 VIP 的 ${vipLift}×` : '資料不足'}
          subheaderTypographyProps={{ fontSize: '0.75rem' }}
        />
        <CardContent sx={{ pt: 0 }}>
          <Grid container spacing={2}>
            {[
              { key: 'vip', label: 'VIP 會員', color: COLORS.vip, data: behavior.vip },
              { key: 'non_vip', label: '非 VIP', color: COLORS.slate, data: behavior.non_vip },
            ].map((row) => (
              <Grid item xs={12} md={6} key={row.key}>
                <Box
                  sx={{
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 2,
                    p: 2,
                    bgcolor: `${row.color}08`,
                  }}
                >
                  <Chip
                    label={row.label}
                    size="small"
                    sx={{
                      bgcolor: `${row.color}22`,
                      color: row.color,
                      fontWeight: 600,
                      mb: 1.5,
                    }}
                  />
                  {row.data ? (
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          客戶數
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                          {formatNumber(row.data.customers)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          訂單數
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                          {formatNumber(row.data.orders)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          訂單 / 客戶
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                          {row.data.orders_per_customer.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          平均客單
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                          {formatCurrency(row.data.avg_order_value)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="caption" color="text.secondary">
                          180 天總營收
                        </Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: row.color }}>
                          {formatCurrency(row.data.revenue)}
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      無資料
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Upcoming renewals */}
      <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}` }}>
        <CardHeader
          title="60 天內到期 VIP"
          titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
          subheader={`${renewals.length} 位需要續約提醒 — 續約前 14 天寄出第一封`}
          subheaderTypographyProps={{ fontSize: '0.75rem' }}
        />
        <CardContent sx={{ pt: 0 }}>
          {renewals.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              目前沒有即將到期的 VIP 會員
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>姓名</TableCell>
                    <TableCell sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>電話</TableCell>
                    <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                      到期日
                    </TableCell>
                    <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                      剩餘天數
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renewals.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell>{c.name || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{c.email || '—'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{c.phone || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                        {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={`${c.days_until_expiry} 天`}
                          sx={{
                            bgcolor:
                              c.days_until_expiry <= 14
                                ? `${COLORS.danger}22`
                                : c.days_until_expiry <= 30
                                ? `${COLORS.warning}22`
                                : `${COLORS.slate}22`,
                            color:
                              c.days_until_expiry <= 14
                                ? COLORS.danger
                                : c.days_until_expiry <= 30
                                ? COLORS.warning
                                : COLORS.slate,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* VIP Discovery Funnel — added 2026-05-20 */}
      {funnel && (
        <Card sx={{ mt: 3 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
                  VIP 探索漏斗（30 天）
                </Typography>
                <Chip
                  size="small"
                  label="新追蹤"
                  sx={{
                    bgcolor: `${COLORS.success}22`,
                    color: COLORS.success,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    height: 18,
                  }}
                />
              </Box>
            }
            subheader="哪個入口把訪客帶到 /vip，又有多少最後付費"
          />
          <CardContent>
            {/* Funnel stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={4}>
                <StatCard
                  icon={<VipIcon />}
                  label="/vip 訪客 (sessions)"
                  value={formatNumber(funnel.funnel.view_sessions)}
                  subtitle="30 日獨立訪客"
                  color={COLORS.slate}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  icon={<AddIcon />}
                  label="付費嘗試"
                  value={formatNumber(funnel.funnel.attempts)}
                  subtitle={
                    funnel.funnel.view_sessions > 0
                      ? `${formatPct((funnel.funnel.attempts / funnel.funnel.view_sessions) * 100)} 點擊加入`
                      : '—'
                  }
                  color={COLORS.warning}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <StatCard
                  icon={<UpgradeIcon />}
                  label="付費成功"
                  value={formatNumber(funnel.funnel.paid)}
                  subtitle={
                    funnel.funnel.attempts > 0
                      ? `${formatPct((funnel.funnel.paid / funnel.funnel.attempts) * 100)} 付費完成`
                      : '—'
                  }
                  color={COLORS.success}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Source breakdown */}
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1.5 }}>
              入口來源（哪個 CTA 帶來最多訪客）
            </Typography>
            {funnel.source_breakdown.length === 0 ? (
              <Typography sx={{ fontSize: '0.8rem', color: COLORS.slate }}>
                目前還沒有資料。新的追蹤從 2026-05-20 才開始收集，需要等幾天才會有有意義的數字。
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.7rem', fontWeight: 700 }}>utm_source</TableCell>
                      <TableCell sx={{ fontSize: '0.7rem', fontWeight: 700 }}>utm_medium</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>Sessions</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>% of total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {funnel.source_breakdown.map((row, i) => {
                      const totalSessions = funnel.source_breakdown.reduce((s, r) => s + r.sessions, 0);
                      const pct = totalSessions > 0 ? (row.sessions / totalSessions) * 100 : 0;
                      return (
                        <TableRow key={`${row.utm_source}-${row.utm_medium}-${i}`}>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{row.utm_source}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{row.utm_medium}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
                            {formatNumber(row.sessions)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
                            {formatPct(pct)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Daily views — simple table for now (chart can come later) */}
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1.5 }}>
              每日 /vip 訪客
            </Typography>
            {funnel.daily_views.every(d => d.unique_sessions === 0) ? (
              <Typography sx={{ fontSize: '0.8rem', color: COLORS.slate }}>
                還沒有任何 /vip 訪客。儀錶板剛部署，需要 24-48 小時才會看到第一筆數據。
              </Typography>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(60px, 1fr))',
                gap: 0.5,
                alignItems: 'end',
                height: 80,
                mb: 1,
              }}>
                {funnel.daily_views.map((d) => {
                  const maxVal = Math.max(1, ...funnel.daily_views.map(x => x.unique_sessions));
                  const h = (d.unique_sessions / maxVal) * 100;
                  return (
                    <Box
                      key={d.date}
                      title={`${d.date}: ${d.unique_sessions} sessions, ${d.views} events`}
                      sx={{
                        height: `${Math.max(h, 2)}%`,
                        bgcolor: d.unique_sessions > 0 ? COLORS.success : `${COLORS.slate}33`,
                        borderRadius: '2px 2px 0 0',
                      }}
                    />
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default VipSection;
